import { NumberInput } from '@/UI/NumberInput/NumberInput';
import { PanelField } from '@/UI/PanelField/PanelField';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { gizmoChannels, gizmoChannelGroups, type ChannelGroup, type GizmoParamType } from './gizmoChannels';
import type { ParameterValue } from 'web';

type VecValue = Extract<ParameterValue, { type: 'vec2' | 'vec3' | 'vec4' }>;

interface GizmoVectorFieldProps {
  type: GizmoParamType;
  value: VecValue;
  label?: string;
  disabled: boolean;
  onChange: (next: ParameterValue) => void;
}

/** The right-panel editor for a gizmo-constrained `vecN` parameter. */
export function GizmoVectorField({ type, value, label, disabled, onChange }: GizmoVectorFieldProps) {
  const channels = gizmoChannels(type);

  const input = (index: number) => {
    const channel = channels[index];
    return (
      <NumberInput
        key={channel.label ?? `ch-${index}`}
        value={value.value[index]}
        prefix={channel.label}
        unit={channel.unit}
        decimals={channel.decimals ?? 0}
        format={channel.format}
        parse={channel.parse}
        disabled={disabled}
        commitOnlyWhenEdited
        onSubmit={(stored) => {
          const next = [...value.value];
          next[index] = stored;
          onChange({ type: value.type, value: next } as VecValue);
        }}
      />
    );
  };

  const row = (group: ChannelGroup) => (
    <FlexCenVer sx={{ gap: 0.5, flexWrap: 'wrap' }}>{group.indices.map(input)}</FlexCenVer>
  );

  const groups = gizmoChannelGroups(type);
  if (groups.length === 1) return row(groups[0]);

  return (
    <FlexCol sx={{ gap: 1, width: '100%' }}>
      {groups.map((group) =>
        group.labelSuffix === undefined ? (
          <FlexCol key="channels">{row(group)}</FlexCol>
        ) : (
          <PanelField
            key={group.labelSuffix}
            label={label === undefined ? group.labelSuffix : `${label} ${group.labelSuffix}`}
            disabled={disabled}
          >
            {row(group)}
          </PanelField>
        ),
      )}
    </FlexCol>
  );
}
